import {
  type DemoCapabilitiesResponse,
  DemoCapabilitiesResponseSchema,
  DemoSessionResponseSchema,
} from "contracts/api/demos";
import { createSignal } from "solid-js";
import { clearSession, restoreSession, sameDemo } from "./admin";
import { request } from "./api-client";

const [capabilities, setCapabilities] =
  createSignal<DemoCapabilitiesResponse | null>(null);
export const demoCapabilities = capabilities;
const [refreshFailed, setRefreshFailed] = createSignal(false);
let expiresAt = 0;
let pending: Promise<DemoCapabilitiesResponse> | null = null;
export const socialEnabled = () =>
  capabilities()?.social === true && !refreshFailed();

export function refreshCapabilities(): Promise<DemoCapabilitiesResponse> {
  if (pending) return pending;
  pending = request("GET", "/api/capabilities", DemoCapabilitiesResponseSchema)
    .then((value) => {
      const previous = capabilities();
      if (previous && !sameDemo(previous, value)) clearSession();
      expiresAt = Date.now() + value.refreshAfterMs;
      setRefreshFailed(false);
      setCapabilities(value);
      return value;
    })
    .catch((error: unknown) => {
      setRefreshFailed(true);
      throw error;
    })
    .finally(() => {
      pending = null;
    });
  return pending;
}

export function startCapabilityRefresh(): () => void {
  const interval = capabilities()?.refreshAfterMs;
  if (!interval) throw new Error("Load capabilities before starting refresh");
  const timer = setInterval(() => {
    void refreshCapabilities().catch(() => undefined);
  }, interval);
  return () => clearInterval(timer);
}

export async function initializeDemo(): Promise<boolean> {
  localStorage.removeItem("faro:gate");
  localStorage.removeItem("faro:admin-secret");
  localStorage.removeItem("faro:query");
  const identity = await refreshCapabilities();
  if (!restoreSession(identity)) return false;
  try {
    const session = await request(
      "GET",
      "/api/session",
      DemoSessionResponseSchema,
    );
    if (sameDemo(session, identity)) return true;
  } catch {
    /* A stored token must pass backend validation before cache restoration. */
  }
  clearSession();
  return false;
}

export function requireSocial(): void {
  if (!socialEnabled() || Date.now() >= expiresAt)
    throw new Error("Social is not available in this environment");
}
